import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";
import { CLAUSES } from "@/lib/contractPdf";
import { PenLine } from "lucide-react";

const CIG_RE = /^[A-Z0-9]{10}$/;
const CUP_RE = /^[A-Z0-9]{15}$/;
const clean = (v) => v.toUpperCase().replace(/[^A-Z0-9]/g, "");

export const SignContractDialog = ({ open, onOpenChange, title, description, summary, withNotes, confirmLabel, onConfirm, testPrefix, paFields, cigRequired }) => {
  const [cig, setCig] = useState("");
  const [cup, setCup] = useState("");
  const [signature, setSignature] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [notes, setNotes] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setSignature(null); setAgree(false); setBusy(false); }
  }, [open]);

  const cigOk = cig ? CIG_RE.test(cig) : !cigRequired;
  const cupOk = !cup || CUP_RE.test(cup);
  const ready = signature && signerName.trim().length >= 3 && agree && !busy && (!paFields || (cigOk && cupOk));
  const submit = async () => {
    setBusy(true);
    try { await onConfirm({ signature, signerName: signerName.trim(), notes: notes.trim() || null, ...(paFields ? { cig: cig || null, cup: cup || null } : {}) }); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid={`${testPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm bg-slate-50 rounded-xl p-4" data-testid={`${testPrefix}-summary`}>
          {summary.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><span className="text-slate-500">{k}</span><span className="font-semibold text-right">{v}</span></div>)}
        </div>
        <div className="max-h-40 overflow-y-auto text-xs text-slate-600 space-y-1.5 border border-slate-200 rounded-xl p-3">
          {CLAUSES.map((c) => <p key={c}>{c}</p>)}
        </div>
        {paFields && (
          <div className="grid sm:grid-cols-2 gap-3" data-testid={`${testPrefix}-pa-fields`}>
            <div>
              <label className="text-xs font-semibold text-slate-600">CIG - Codice Identificativo Gara {cigRequired ? <span className="text-gold">*</span> : "(facoltativo)"}</label>
              <input data-testid={`${testPrefix}-cig`} value={cig} maxLength={10} onChange={(e) => setCig(clean(e.target.value))} placeholder="10 caratteri" className={`w-full mt-1 border rounded-xl px-3 py-2 text-sm font-mono ${cig && !cigOk ? "border-red-400" : "border-slate-300"}`}/>
              {cig && !cigOk && <p className="text-xs text-red-600 mt-1">Il CIG è di 10 caratteri alfanumerici</p>}
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">CUP - Codice Unico di Progetto (facoltativo)</label>
              <input data-testid={`${testPrefix}-cup`} value={cup} maxLength={15} onChange={(e) => setCup(clean(e.target.value))} placeholder="15 caratteri" className={`w-full mt-1 border rounded-xl px-3 py-2 text-sm font-mono ${!cupOk ? "border-red-400" : "border-slate-300"}`}/>
              {!cupOk && <p className="text-xs text-red-600 mt-1">Il CUP è di 15 caratteri alfanumerici</p>}
            </div>
          </div>
        )}
        {withNotes && (
          <textarea data-testid={`${testPrefix}-notes`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} maxLength={3000}
            placeholder="Condizioni particolari (facoltativo): orari, scaletta, impianto, catering…"
            className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm"/>
        )}
        <input data-testid={`${testPrefix}-name`} value={signerName} onChange={(e) => setSignerName(e.target.value)} placeholder="Nome e cognome del firmatario"
          className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm"/>
        <SignaturePad onChange={setSignature} testId={`${testPrefix}-pad`} />
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" data-testid={`${testPrefix}-agree`} checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-[#D5A34C]"/>
          <span>Ho letto e accetto le condizioni del contratto e confermo la mia firma.</span>
        </label>
        <button data-testid={`${testPrefix}-confirm`} disabled={!ready} onClick={submit}
          className="w-full py-3 px-4 bg-gold hover:bg-gold-600 text-navy font-bold rounded-xl shadow-lg flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity">
          <PenLine className="w-5 h-5"/>{busy ? "Invio…" : confirmLabel}
        </button>
      </DialogContent>
    </Dialog>
  );
};
